import { useEffect, useState } from 'react'

import { Icon, Select, Switch, Typography } from 'antd'

import { getMessage } from '../../../../../../../util/IntlMessages'
import { useActionType } from '../../../hooks/useActionType'
import { useManageDataSourceOptions } from '../../../hooks/useManageDataSourceOptions'
import { useManageField } from '../../../hooks/useManageFields'
import { useGetFilterOptionsQuery } from '../../../services/dataSource'
import { useUpdateFormFieldMutation } from '../../../services/formField'

import '../styles.less'

function FilterSelect({ column, value, onChange }) {
  const { data: options = [] } = useGetFilterOptionsQuery(column.filterSourceUrl, {
    skip: !column.filterSourceUrl
  })

  return (
    <div className="dataSource__filters">
      <label>{column.label}</label>
      <Select
        allowClear
        mode="multiple"
        value={value ?? []}
        onChange={onChange}
        filterOption={(input, option) =>
          String(option?.props?.label ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      >
        {options.map((opt, index) => {
          const optionKey =
            typeof opt.value === 'string' || typeof opt.value === 'number'
              ? String(opt.value)
              : `${opt.description || 'option'}-${index}`

          return (
            <Select.Option value={opt.value} key={optionKey} label={opt.description}>
              {opt.description}
            </Select.Option>
          )
        })}
      </Select>
    </div>
  )
}

export function DataSourceComponent() {
  const { selectedFieldId, updateField, currentField, fields } = useManageField()
  const { formId } = useActionType()
  const [updateFormField] = useUpdateFormFieldMutation()

  const [dataSourceSelectedId, setDataSourceSelectedId] = useState(undefined)
  const [applyFilters, setApplyFilters] = useState(false)
  const [filters, setFilters] = useState({})

  const { dataSourceOptions, loadingDataSource } = useManageDataSourceOptions()
  const dataSourceSelected = dataSourceOptions?.find((source) => source.id === dataSourceSelectedId) ?? null
  const filterColumns = (dataSourceSelected?.columns ?? []).filter((column) => column.isFilterable)
  const attributeColumns = (dataSourceSelected?.columns ?? []).filter((column) => column.isAttribute)

  const siblingFields = (fields?.ids ?? [])
    .map((id) => fields.entities[id])
    .filter(
      (field) =>
        field &&
        !field.isGroup &&
        field.id !== currentField?.id &&
        field.fieldGroup === currentField?.fieldGroup &&
        field.active
    )

  const relatedChildren = siblingFields.filter((field) => field.dataSourceFieldId === currentField?.id)

  useEffect(() => {
    if (!currentField || !currentField.dataSourceOptions) return

    if (currentField.dataSourceOptions.dataSourceEnumId) {
      setDataSourceSelectedId(currentField.dataSourceOptions.dataSourceEnumId)
    } else {
      setDataSourceSelectedId(undefined)
    }

    if (currentField.dataSourceOptions.filters) {
      try {
        const savedFilters =
          typeof currentField.dataSourceOptions.filters === 'string'
            ? JSON.parse(currentField.dataSourceOptions.filters)
            : currentField.dataSourceOptions.filters

        if (savedFilters && Object.keys(savedFilters).length) {
          setApplyFilters(true)
          setFilters(savedFilters)
          return
        }
      } catch (error) {
        console.error('Erro ao carregar filtros da fonte de dados', error)
      }
    } else {
      setApplyFilters(false)
      setFilters({})
    }
  }, [currentField, dataSourceOptions])

  const persistFilters = (nextFilters, enabled) => {
    const dataSourceOptionsPayload = {
      filters: JSON.stringify(enabled ? nextFilters : {}),
      dataSourceEnumId: dataSourceSelectedId
    }
    updateField({ property: 'dataSourceOptions', value: dataSourceOptionsPayload, fieldId: selectedFieldId })
  }

  const updateFilter = (column) => (selectedValues) => {
    const newFilters = { ...filters, [column.alias]: selectedValues || [] }
    setFilters(newFilters)
    persistFilters(newFilters, applyFilters)
  }

  const updateDataSource = (sourceId) => {
    setDataSourceSelectedId(sourceId)
    setFilters({})
    setApplyFilters(false)
    updateField({
      property: 'dataSourceOptions',
      value: { dataSourceEnumId: sourceId },
      fieldId: selectedFieldId
    })
  }

  const updateApplyFilters = (checked) => {
    setApplyFilters(checked)
    persistFilters(filters, checked)
  }

  const patchChild = (childField, changes) => {
    updateFormField({ formId, payload: { ...childField, ...changes } })
  }

  const relateChild = (childField) => patchChild(childField, { dataSourceFieldId: currentField.id })

  const unrelateChild = (childField) => patchChild(childField, { dataSourceFieldId: null, dataSourceColumn: null })

  const setChildColumn = (childField) => (alias) =>
    patchChild(childField, { dataSourceFieldId: currentField.id, dataSourceColumn: alias || null })

  return (
    <div className="dataSource">
      {loadingDataSource && <Loading />}
      <>
        <label>{getMessage('dataSourceTitle')}</label>
        <Select allowClear value={dataSourceSelectedId} onChange={updateDataSource}>
          {dataSourceOptions?.map((source) => (
            <Select.Option value={source.id} key={source.id} label={source.description}>
              {source.description}
            </Select.Option>
          ))}
        </Select>
      </>

      <div className="dataSource__dataSourceSelectedContainer">
        {dataSourceSelected && filterColumns.length > 0 && (
          <div style={{ display: 'flex' }}>
            <Typography style={{ fontWeight: 'bold' }}>{getMessage('dataSourceApplyFilters')}</Typography>
            <Switch style={{ marginLeft: 10 }} checked={applyFilters} onChange={updateApplyFilters} size="small" />
          </div>
        )}

        {applyFilters && (
          <div className="dataSource__filterContainer">
            {filterColumns.map((column) => (
              <FilterSelect
                key={column.id}
                column={column}
                value={filters[column.alias]}
                onChange={updateFilter(column)}
              />
            ))}
          </div>
        )}

        {dataSourceSelected && siblingFields.length > 0 && attributeColumns.length > 0 && (
          <div className="dataSource__relatedFields">
            <Typography style={{ fontWeight: 'bold', marginTop: 10 }}>
              {getMessage('dataSourceRelatedFields')}
            </Typography>

            <Select
              mode="multiple"
              placeholder={getMessage('select')}
              value={relatedChildren.map((child) => child.id)}
              onSelect={(id) => relateChild(fields.entities[id])}
              onDeselect={(id) => unrelateChild(fields.entities[id])}
              filterOption={(input, option) => option.props.label.toLowerCase().includes(input.toLowerCase())}
            >
              {siblingFields.map((child) => (
                <Select.Option value={child.id} key={child.id} label={child.fieldName}>
                  {child.fieldName}
                </Select.Option>
              ))}
            </Select>

            <Typography
              style={{ fontWeight: 'bold', marginTop: 5, display: !relatedChildren.length ? 'none' : undefined }}
            >
              {getMessage('dataSourceLinkAttributes', dataSourceSelected.description)}
            </Typography>

            {relatedChildren.map((child) => (
              <div key={child.id} className="dataSource__filters">
                <label>{child.fieldName}</label>
                <Select
                  allowClear
                  placeholder={getMessage('select')}
                  value={child.dataSourceColumn ?? undefined}
                  onChange={setChildColumn(child)}
                >
                  {attributeColumns.map((column) => (
                    <Select.Option value={column.alias} key={column.alias} label={column.label || column.alias}>
                      {column.label || column.alias}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const Loading = () => (
  <div className="selectOptions__loadingWrapper">
    <Icon type="loading" className="selectOptions__loadingIcon" spin />
    <Typography className="selectOptions__loadingText">{getMessage('loading...')}</Typography>
  </div>
)
